/**
 * The card chrome every Integrations panel wears.
 *
 * Seven unrelated services were seven hand-written bordered boxes with the same
 * head in slightly different words. One shell instead: the CRM's card, its head
 * row, and the connection state as a `StatusPill` on the right - never a badge,
 * and never colour on its own, because the pill's dot is the part that survives
 * a bad monitor.
 *
 * `crm-card` carries no padding of its own - it is a surface, nothing more - so
 * the body brings its own gutter, matching the head's 26px.
 */
import type { ReactNode } from "react";
import { Loader2, type LucideIcon } from "lucide-react";
import { cn } from "@wa/ui";
import { Card, CardHead, StatusPill } from "../../../ui";

/**
 * A card head's title with the service's own mark in front of it.
 *
 * Exported because the caller-id tab's list card wears the same head but not
 * this shell: `crm-list-head` and `crm-rows` bring their own gutters, and the
 * body wrapper below would add a second one.
 */
export function HeadTitle({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <span className="flex items-center gap-2.5">
      <Icon className="size-[17px] shrink-0" style={{ color: "var(--color-primary)" }} />
      <span className="crm-trunc">{children}</span>
    </span>
  );
}

export function IntegrationCard({
  icon,
  title,
  desc,
  status,
  bodyClassName,
  children,
}: {
  icon: LucideIcon;
  title: string;
  desc?: string;
  /** Usually a `ConnStatus`; anything that belongs on the head's right edge. */
  status?: ReactNode;
  bodyClassName?: string;
  children?: ReactNode;
}) {
  return (
    <Card>
      <CardHead title={<HeadTitle icon={icon}>{title}</HeadTitle>} sub={desc} actions={status} />
      {children ? <div className={cn("px-[26px] pb-6 pt-3.5", bodyClassName)}>{children}</div> : null}
    </Card>
  );
}

/**
 * Configured or not, as the head's status pill.
 *
 * `mut` rather than a red tone for "not configured": an integration nobody has
 * set up yet is not broken, and painting it as a fault teaches operators to
 * ignore the colour on the ones that are.
 */
export function ConnStatus({ ok, okLabel, offLabel }: { ok: boolean; okLabel: string; offLabel: string }) {
  return <StatusPill tone={ok ? "ok" : "mut"}>{ok ? okLabel : offLabel}</StatusPill>;
}

/** What a panel shows while its first GET is in flight. A centred spinner on the
 *  card it is about to fill, so the page does not jump when the data lands. */
export function PanelLoader() {
  return (
    <div className="crm-card flex justify-center py-16 text-muted-foreground">
      <Loader2 className="size-6 animate-spin" />
    </div>
  );
}
